import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import CssBaseline from '@mui/material/CssBaseline'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import { Box, IconButton, Typography } from '@mui/material'
import TokTokDocSnackbar from '../components/Common/Snackbar'
import ErrorBoundary from '../components/Common/ErrorBoundary'
import Cgu from '../components/Cgu/Cgu'
import Header from '../components/feature/header/Header'
import Container from '../components/MainPage/Container'
import useCurrentHcs from '../hooks/useCurrentHcs'
import useStyles from './_styles'
import { MainProps } from './_props'
import FullScreenCalloutAccessMode from '../components/feature/fullScreenCalloutAccessMode/FullScreenCalloutAccessMode'
import useCurrentUser from '../hooks/useCurrentUser'
import { HsAccessModeEnum } from '../components/feature/fullScreenCalloutAccessMode/_props'
import { CurrentHcsGuard } from '../components/feature/hcs/CurrentHcsProvider/CurrentHcsGuard'
import useTTDFlag from '../hooks/useTTDFlag'
import { HelpCenterDialogProvider } from '../context/HelpCenterDialogContext'

const Main = ({ noBackground, title, goBackTo, children, callout, tunnelMode }: MainProps) => {
    const navigate = useNavigate()
    const location = useLocation()
    const classes = useStyles()

    const { accessMode, name: hcsName, isCurrentUserCertified, displayPgssiBanner } = useCurrentHcs()
    const isPgssiBannersEnabled = useTTDFlag('pgssi_banners')
    const displayFullScreenCallout = Boolean(isPgssiBannersEnabled && displayPgssiBanner)
    const { hasAgreedLatestCGUs } = useCurrentUser()

    const { pathname } = useLocation()

    React.useEffect(() => {
        window.scrollTo(0, 0)
    }, [pathname])

    useEffect(() => {
        if (!isCurrentUserCertified) navigate('/me')
    }, [isCurrentUserCertified, navigate])

    const handleGoBack = () => {
        if (typeof goBackTo === 'function') {
            const args = location.pathname.split('/')
            const path = goBackTo(args[args.length - 1])
            navigate(path)
        } else if (goBackTo !== undefined) {
            navigate(goBackTo)
        }
    }

    return (
        <CurrentHcsGuard>
            <div className={classes.appContainer}>
                <CssBaseline />
                <ErrorBoundary>
                    <HelpCenterDialogProvider>
                        <Header tunnelMode={tunnelMode} />
                    </HelpCenterDialogProvider>
                    {!tunnelMode && hasAgreedLatestCGUs && displayFullScreenCallout && (
                        <Box className={classes.appContainerFullScreenCalloutContainer}>
                            <FullScreenCalloutAccessMode mode={accessMode.toLowerCase() as HsAccessModeEnum} />
                        </Box>
                    )}
                </ErrorBoundary>
                <ErrorBoundary>
                    {title && hasAgreedLatestCGUs && (
                        <Box className={classes.appContainerTitleContainer}>
                            {goBackTo && isCurrentUserCertified && (
                                <IconButton
                                    className={classes.appContainerTitleGoBack}
                                    onClick={handleGoBack}
                                    data-cy="dataCy_appGoBackButton"
                                >
                                    <ChevronLeftRoundedIcon />
                                </IconButton>
                            )}
                            <Box className={classes.appContainerTitleTextContainer}>
                                <Typography className={classes.appContainerTitleHcs}>{hcsName}</Typography>
                                <Typography className={classes.appContainerTitle}>{title}</Typography>
                            </Box>
                        </Box>
                    )}

                    {callout}
                    <Container noBackground={noBackground}>{hasAgreedLatestCGUs ? children : <Cgu />}</Container>
                </ErrorBoundary>
                <ErrorBoundary>
                    <TokTokDocSnackbar />
                </ErrorBoundary>
            </div>
        </CurrentHcsGuard>
    )
}

export default Main
